// src/pages/CheckoutPage.js
import React, { useEffect, useState, useContext } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import { CartContext } from '../context/CartContext';
import AuthContext from '../context/AuthContext';
import './CheckoutPage.css';

/**
 * Same discount logic from CartPage
 */
function calcDiscountedPrice(basePrice, discount) {
    if (!discount) return basePrice;
    const discountAmount = parseFloat(discount.discountAmount) || 0;
    const discountPercent = parseFloat(discount.discountPercent) || 0;
    let price = basePrice;
    if (discountPercent > 0) {
        price = price * (1 - discountPercent);
    } else if (discountAmount > 0) {
        price -= discountAmount;
        if (price < 0) price = 0;
    }
    return price;
}

const CheckoutPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { loadCart } = useContext(CartContext);
    const { user } = useContext(AuthContext);

    // Data passed from CartPage
    const {
        notes = '',
        subtotalBeforeDiscount = 0,
        totalDiscount = 0,
        subtotalAfterDiscount = 0,
        taxAmount = 0,
        grandTotal = 0,
        appliedDiscount = null,
        userTaxRate = 0.07,
        cart: cartFromState = [],
        preauthorizationTotal = 0,
        preauthorizationItems = []
    } = location.state || {};

    // Rental policy
    const [policy, setPolicy] = useState(null);
    const [loadingPolicy, setLoadingPolicy] = useState(true);
    const [error, setError] = useState(null);

    // Payment method
    const [selectedMethod, setSelectedMethod] = useState('');
    const [payInStoreForRemainder, setPayInStoreForRemainder] = useState(false);

    // Customer info
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');

    // Payment error
    const [paymentError, setPaymentError] = useState(null);

    // PayPal key
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

    // Prevent duplicate checkout submissions
    const [isSubmittingReservation, setIsSubmittingReservation] = useState(false);

    // Load user info
    useEffect(() => {
        if (cartFromState.length === 0) {
            // Optionally navigate('/cart') if empty
        }
        if (user) {
            setCustomerName(`${user.firstName} ${user.lastName}`);
            setCustomerEmail(user.email);
            setCustomerPhone(user.phone);
        }
    }, [cartFromState, user]);

    // Fetch rental policy
    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const resp = await api.get('/store/rental-policy');
                setPolicy(resp.data);
            } catch (err) {
                console.error('Failed to load rental policy:', err);
                setError('Failed to load rental policy.');
            } finally {
                setLoadingPolicy(false);
            }
        };
        fetchPolicy();
    }, []);

    if (loadingPolicy) return <p>Loading policy...</p>;
    if (error) return <p style={{ color: 'red' }}>{error}</p>;
    if (!policy) return <p>No policy found.</p>;

    /**
     * Calculate the total discounted cost for the entire cart (pre-tax).
     * Remember that cartFromState[x].unitPrice is the total for that item
     * (i.e. nights * daily rate).
     */
    function getTotalDiscountedCost() {
        let total = 0;
        for (const ci of cartFromState) {
            const base = parseFloat(ci.unitPrice);
            const discounted = ci.id === 'insurance-line'
                 ? base                          // never discount insurance
                 : calcDiscountedPrice(base, appliedDiscount);

            total += discounted;
        }
        return total;
    }

    /**
     * If the store uses a nonrefundable deposit, compute it for the entire cart.
     */
    function getEffectiveNonrefundableFee() {
        if (!policy.useNonrefundableFee) {
            return 0;
        }
        const baseFee = policy.nonrefundableFeeAmount;

        // Not per-item => single flat
        if (!policy.useNonrefundableFeePerItem) {
            let fee = baseFee;
            // If only 1 line
            if (cartFromState.length === 1) {
                const singleItem = cartFromState[0];
                const base = parseFloat(singleItem.unitPrice);
                const discounted = calcDiscountedPrice(base, appliedDiscount);

                if (discounted < fee) {
                    fee = 0;
                }
                if (grandTotal < fee) {
                    fee = 0;
                }
            }
            return fee;
        }

        // Per-item logic
        let countEligible = 0;
        for (const ci of cartFromState) {
            if (!ci.ignoreNonRefundableReservationFee) {
                countEligible++;
            }
        }
        let fee = baseFee * countEligible;

        if (cartFromState.length === 1) {
            const singleItem = cartFromState[0];
            const base = parseFloat(singleItem.unitPrice);
            const discounted = calcDiscountedPrice(base, appliedDiscount);

            if (discounted < fee) {
                fee = 0;
            }
            if (grandTotal < fee) {
                fee = 0;
            }
        }
        return fee;
    }

    /**
     * Builds Lightspeed workorder items.
     *
     * Cart items may store unitQuantity as rental nights, but for Lightspeed
     * workorder items we always send:
     * - unitQuantity = 1
     * - unitPrice = full line amount that should appear in Lightspeed
     *
     * For deposit-only scenarios, unitPrice is reduced proportionally so
     * Lightspeed reflects the remaining pretax balance due in store.
     *
     */
    function buildLightspeedItems() {
        const forceOnlyNRFeeOnline = (
            policy.useNonrefundableFee &&
            policy.onlyCollectNonrefundableFeeOnline &&
            policy.allowOnlinePayment
        );

        let depositNow = 0;
        if (
            selectedMethod === 'PAYPAL' &&
            policy.useNonrefundableFee &&
            (payInStoreForRemainder || forceOnlyNRFeeOnline)
        ) {
            depositNow = getEffectiveNonrefundableFee();
        }

        const totalDiscountedAll = getTotalDiscountedCost();

        const leftoverWithTax = Math.max(grandTotal - depositNow, 0);
        const leftoverPretaxCart = leftoverWithTax / (1 + userTaxRate);

        return cartFromState.map(ci => {
            const base = parseFloat(ci.unitPrice);
            const discountedLinePrice = ci.id === 'insurance-line'
                ? base
                : calcDiscountedPrice(base, appliedDiscount);

            let finalLineTotal = discountedLinePrice;
            if (depositNow > 0 && totalDiscountedAll > 0) {
                const proportion = discountedLinePrice / totalDiscountedAll;
                finalLineTotal = leftoverPretaxCart * proportion;
            }

            return {
                employeeID: ci.employeeID,
                shopID: ci.shopID,
                itemID: ci.itemID,
                unitPrice: finalLineTotal.toFixed(2),   // full line price
                customerID: ci.customerId,
                unitQuantity: "1",                      // workorder item quantity always 1
                startDate: ci.startDate,
                endDate: ci.endDate,
                imageUrl: ci.imageUrl,
                itemDescription: ci.itemDescription,
                customSku: ci.customSku,
                ignoreNonRefundableReservationFee: ci.ignoreNonRefundableReservationFee
            };
        });
    }

    // In-Store Payment
    const handleInStoreCheckout = async () => {
        if (isSubmittingReservation) {
            return;
        }

        setIsSubmittingReservation(true);
        setPaymentError(null);

        if (selectedMethod !== 'IN_STORE') {
            setPaymentError("Please select 'In-Store' method.");
            setIsSubmittingReservation(false);
            return;
        }

        try {
            const lightspeedItems = buildLightspeedItems();
            const nonrefundableFeeCharged = getEffectiveNonrefundableFee();

            const payload = {
                paymentMethod: 'IN_STORE',
                currency: 'USD',
                payInStoreForRemainder: false,
                customerName,
                address1 : user.address1,
                address2 : user.address2,
                city     : user.city,
                state    : user.state,
                zip      : user.zip,
                customerEmail,
                customerPhone,
                notes,
                subtotalBeforeDiscount,
                totalDiscount,
                subtotalAfterDiscount,
                taxAmount,
                grandTotal,
                userTaxRate,
                lightspeedItems,
                nonrefundableFeeCharged,
                preauthorizationTotal,
                preauthorizationItems
            };

            const resp = await api.post('/checkout', payload);

            if (!resp.data.success) {
                setPaymentError(
                    "We couldn't complete the in-store checkout. " +
                    "Please try again or contact us. (Details: " + resp.data.message + ")"
                );
                setIsSubmittingReservation(false);
                return;
            }

            loadCart([]);
            navigate('/confirmation');

        } catch (err) {
            console.error("In-store checkout error:", err);
            setPaymentError(
                "Your in-store checkout could not be completed. Please try again or contact support."
            );
            setIsSubmittingReservation(false);
        }
    };

    // PayPal Buttons
    const renderPayPalButtons = () => {
        const forceOnlyNRFeeOnline = (
            policy.useNonrefundableFee &&
            policy.onlyCollectNonrefundableFeeOnline &&
            policy.allowOnlinePayment
        );

        let amountToCharge = grandTotal;
        if (
            policy.useNonrefundableFee &&
            (forceOnlyNRFeeOnline || (policy.allowInStorePayment && payInStoreForRemainder))
        ) {
            amountToCharge = getEffectiveNonrefundableFee();
        }

        const lightspeedItems = buildLightspeedItems();

        return (
            <PayPalScriptProvider options={{ 'client-id': clientId }}>
                <PayPalButtons
                    disabled={isSubmittingReservation}
                    style={{ layout: 'vertical' }}
                    createOrder={(data, actions) => {
                        return actions.order.create({
                            purchase_units: [{
                                amount: { value: amountToCharge.toFixed(2) }
                            }]
                        });
                    }}
                    onApprove={async (data, actions) => {
                        if (isSubmittingReservation) {
                            return;
                        }

                        setIsSubmittingReservation(true);
                        setPaymentError(null);

                        try {
                            const details = await actions.order.capture();
                            console.log('PayPal capture details:', details);

                            const captureId = details.purchase_units[0].payments.captures[0].id;
                            const nonrefundableFeeCharged = getEffectiveNonrefundableFee();

                            const payload = {
                                paymentMethod: 'PAYPAL',
                                currency: 'USD',
                                payInStoreForRemainder,
                                customerName,
                                address1 : user.address1,
                                address2 : user.address2,
                                city     : user.city,
                                state    : user.state,
                                zip      : user.zip,
                                customerEmail,
                                customerPhone,
                                notes,
                                paypalCaptureId: captureId,
                                subtotalBeforeDiscount,
                                totalDiscount,
                                subtotalAfterDiscount,
                                taxAmount,
                                grandTotal,
                                userTaxRate,
                                lightspeedItems,
                                nonrefundableFeeCharged,
                                preauthorizationTotal,
                                preauthorizationItems
                            };

                            const resp = await api.post('/checkout', payload);

                            if (!resp.data.success) {
                                setPaymentError(
                                    "We couldn't validate your payment after PayPal authorization. " +
                                    "Please try again or use a different payment method. (Details: " + resp.data.message + ")"
                                );
                                setIsSubmittingReservation(false);
                                return;
                            }

                            loadCart([]);
                            navigate('/confirmation');

                        } catch (finalizeErr) {
                            console.error('PayPal finalize error:', finalizeErr);
                            setPaymentError(
                                "Your payment was authorized but we couldn't finalize. Please contact support or try again."
                            );
                            setIsSubmittingReservation(false);
                        }
                    }}
                    onCancel={() => {
                        console.warn('PayPal payment canceled.');
                        setPaymentError(
                            "Payment was canceled. You may select a different method or try again."
                        );
                    }}
                    onError={(err) => {
                        console.error('PayPal payment error:', err);
                        let message = "We couldn't validate your payment. Please verify your card or PayPal account and try again.";
                        if (typeof err === 'object' && err.message) {
                            if (err.message.includes("DECLINED")) {
                                message =
                                    "Your payment was declined by your bank or card issuer. " +
                                    "Please try another payment method.";
                            } else if (
                                err.message.includes("VALIDATION_ERROR") ||
                                err.message.includes("OAS_VALIDATION_ERROR")
                            ) {
                                message =
                                    "There was an issue validating your credit card details. " +
                                    "Please double-check and try again.";
                            }
                        }
                        setPaymentError(message);
                    }}
                />
            </PayPalScriptProvider>
        );
    };

    // Possibly hide In-Store if policy says onlyCollectNonrefundableFeeOnline
    const hideInStoreOption = policy.onlyCollectNonrefundableFeeOnline;

    // For summary
    const effectiveFee = getEffectiveNonrefundableFee();

    return (
        <div className="checkout-container">
            <h1>Checkout</h1>

            {paymentError && (
                <div className="payment-error-box">
                    {paymentError}
                </div>
            )}

            {/* Totals */}
            <div className="checkout-totals">
                <p>
                    <span>Subtotal (Before Discount):</span>
                    <span>${subtotalBeforeDiscount.toFixed(2)}</span>
                </p>
                {totalDiscount > 0 && (
                    <p>
                        <span>Discount:</span>
                        <span>-${totalDiscount.toFixed(2)}</span>
                    </p>
                )}
                <p>
                    <span>Subtotal (After Discount):</span>
                    <span>${subtotalAfterDiscount.toFixed(2)}</span>
                </p>
                <p>
                    <span>Tax:</span>
                    <span>${taxAmount.toFixed(2)}</span>
                </p>
                <p>
                    <strong>Grand Total:</strong>
                    <strong>${grandTotal.toFixed(2)}</strong>
                </p>

                {preauthorizationTotal > 0 && (
                    <div className="checkout-preauth-box">
                        <strong>Preauthorization Hold:</strong>
                        <span>${preauthorizationTotal.toFixed(2)}</span>
                        <p>
                            This amount is not charged now. It may only be used if rental equipment
                            is not returned, is severely damaged, or is considered a total loss.
                        </p>
                    </div>
                )}

                {/* Nonrefundable deposit display */}
                {policy.useNonrefundableFee && effectiveFee > 0 && (
                    <div className="cart-summary-row" style={{ marginTop: '0.75em', fontWeight: 'bold' }}>
                        <span>Reservation Deposit:</span>
                        <span>${effectiveFee.toFixed(2)}</span>
                    </div>
                )}
            </div>

            <div className="payment-method-section">
                <h2>Select Payment Method</h2>

                {policy.onlyCollectNonrefundableFeeOnline && (
                    <p style={{ color: 'blue', fontWeight: 'bold' }}>
                        This store only collects the reservation deposit online.
                        You will pay the remaining balance in-store upon pickup.
                    </p>
                )}

                {/* Online Payment */}
                {policy.allowOnlinePayment && (
                    <>
                        <label>
                            <input
                                type="radio"
                                name="paymentMethod"
                                value="PAYPAL"
                                checked={selectedMethod === 'PAYPAL'}
                                onChange={() => setSelectedMethod('PAYPAL')}
                            />
                            Pay with Credit, Debit, or PayPal
                        </label>

                        {/* Partial deposit sub-option */}
                        {(selectedMethod === 'PAYPAL'
                            && policy.useNonrefundableFee
                            && policy.allowInStorePayment
                            && !policy.onlyCollectNonrefundableFeeOnline) && (
                            <div className="payment-method-suboption">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={payInStoreForRemainder}
                                        onChange={(e) => setPayInStoreForRemainder(e.target.checked)}
                                    />
                                    Pay only the reservation deposit now, pay remainder in store
                                </label>
                                {payInStoreForRemainder && effectiveFee > 0 && (
                                    <p style={{ color: 'green' }}>
                                        Reservation Deposit = ${effectiveFee.toFixed(2)} <br />
                                        You'll pay the rest in-store at pickup.
                                    </p>
                                )}
                                {payInStoreForRemainder && effectiveFee === 0 && (
                                    <p style={{ color: 'green' }}>
                                        No reservation deposit is required for your item(s).<br />
                                        You will pay the full amount in-store.
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* In-store Payment */}
                {!hideInStoreOption && policy.allowInStorePayment && (
                    <div style={{ marginTop: '1em' }}>
                        <label>
                            <input
                                type="radio"
                                name="paymentMethod"
                                value="IN_STORE"
                                checked={selectedMethod === 'IN_STORE'}
                                onChange={() => {
                                    setSelectedMethod('IN_STORE');
                                    setPayInStoreForRemainder(false);
                                }}
                            />
                            Pay In-Store
                        </label>
                        {policy.useNonrefundableFee && selectedMethod === 'IN_STORE' && effectiveFee > 0 && (
                            <p style={{ color: 'green', marginLeft: '2em' }}>
                                Note: The reservation deposit of ${effectiveFee.toFixed(2)} is nonrefundable
                                if you cancel within {policy.nonrefundableFeeWindowHours} hours of your pickup date.
                            </p>
                        )}
                        {policy.useNonrefundableFee && selectedMethod === 'IN_STORE' && effectiveFee === 0 && (
                            <p style={{ color: 'green', marginLeft: '2em' }}>
                                No reservation deposit is charged for your item(s).
                            </p>
                        )}
                    </div>
                )}

                {/* If no payment methods */}
                {!policy.allowOnlinePayment && !policy.allowInStorePayment && (
                    <p style={{ color: 'red' }}>No payment methods currently available.</p>
                )}
            </div>

            <div className="checkout-userinfo">
                <label>Full Name:</label><br/>
                <input
                    type="text"
                    value={customerName}
                    readOnly
                    style={{width: '100%', marginBottom: '1em'}}
                />

                <label>Email:</label><br/>
                <input
                    type="email"
                    value={customerEmail}
                    readOnly
                    style={{width: '100%'}}
                />

                <label>Phone:</label><br/>
                <input
                    type="phone"
                    value={customerPhone}
                    readOnly
                    style={{width: '100%'}}
                />
            </div>

            {selectedMethod === 'PAYPAL' && (
                <div className="paypal-button-container">
                    {renderPayPalButtons()}
                </div>
            )}

            <div className="button-container">
                {selectedMethod === 'IN_STORE' && (
                    <button
                        className="instore-button"
                        onClick={handleInStoreCheckout}
                        disabled={isSubmittingReservation}
                    >
                        {isSubmittingReservation ? 'Creating Reservation...' : 'Proceed with Reservation'}
                    </button>
                )}

                <button
                    onClick={() => navigate(`/cart`)}
                    className="go-back-to-cart-btn"
                    disabled={isSubmittingReservation}
                >
                    Back to Cart
                </button>
            </div>
        </div>
    );
};

export default CheckoutPage;